/**
 * <announcement-rotator>
 * Auto-rotates stacked announcement items with a smooth cross-fade.
 * - No navigation controls.
 * - Interval from data-speed (seconds, clamped to 4-6s).
 * - Pauses on hover/focus only on hover-capable (desktop) devices; mobile
 *   rotates continuously.
 * - Pauses while the tab is hidden and respects prefers-reduced-motion
 *   (rotation still happens, motion is removed via CSS).
 * Items are stacked in a CSS grid cell so switching never shifts layout.
 */
if (!customElements.get('announcement-rotator')) {
  customElements.define(
    'announcement-rotator',
    class AnnouncementRotator extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(
          this.querySelectorAll('.announcement-rotator__item')
        );
        if (this.items.length <= 1) return;

        const speed = parseFloat(this.dataset.speed) || 5;
        this.interval = Math.min(Math.max(speed, 4), 6) * 1000;
        this.autoplay = this.dataset.autoplay === 'true';
        this.index = this.items.findIndex((el) =>
          el.classList.contains('is-active')
        );
        if (this.index < 0) this.index = 0;

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.classList.add('is-reduced-motion');
        }

        this.advance = this.advance.bind(this);

        if (window.matchMedia('(hover: hover)').matches) {
          this.addEventListener('mouseenter', () => this.stop());
          this.addEventListener('mouseleave', () => this.resume());
          this.addEventListener('focusin', () => this.stop());
          this.addEventListener('focusout', () => this.resume());
        }

        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this.stop();
          else this.resume();
        });

        this.resume();
      }

      disconnectedCallback() {
        this.stop();
      }

      resume() {
        if (this.autoplay && !this.timer) {
          this.timer = setInterval(this.advance, this.interval);
        }
      }

      stop() {
        if (this.timer) {
          clearInterval(this.timer);
          this.timer = null;
        }
      }

      advance() {
        const current = this.items[this.index];
        this.index = (this.index + 1) % this.items.length;
        const next = this.items[this.index];

        current.classList.remove('is-active');
        current.setAttribute('aria-hidden', 'true');
        next.classList.add('is-active');
        next.removeAttribute('aria-hidden');
      }
    }
  );
}
